import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $downloadStatementStore, $initialCurrenciesAvailableStore, $profileFeatureStore, $statementsStore } from '@lib/store';
import { useListState, useRouteHook } from '@shared/hooks';
import { cn, PaletteUtils, RoutePaths } from '@shared/utils';
import { type StatementsResponse, StatementType } from '@shared/api';
import { ConsolidateTable } from '../statementsConsolidateTable';
import { AccountTable } from '../statementsAccountTable';
import { CardTable } from '../statementsCardTable';
import { Select, Tabs } from '@shared/widgets';

export const StatementsCardTab = () => {
  const { navigate } = useRouteHook();

  const profileFeatureStore = useUnit($profileFeatureStore.store);
  const currenciesAvailableStore = useUnit($initialCurrenciesAvailableStore.store);
  const statementsStore = useUnit($statementsStore.store);
  const downloadStatementStore = useUnit($downloadStatementStore.store);
  const statementFlow = profileFeatureStore.data?.statements;

  const [tab, setTab] = useState<StatementType>(StatementType.Consolidated);
  const [currency, setCurrency] = useState<string>();

  const statementsConsolidatedState = useListState({ list: 'statementsConsolidated' });
  const statementsAccountState = useListState({ list: 'statementsAccount' });
  const statementsCardState = useListState({ list: 'statementsCard' });
  const stateMap = {
    [StatementType.Consolidated]: statementsConsolidatedState,
    [StatementType.Account]: statementsAccountState,
    [StatementType.Card]: statementsCardState,
  };

  const { t } = useTranslation('statements');

  useEffect(() => {
    if (statementFlow?.viewOrDownloadConsolidatedStatement) setTab(StatementType.Consolidated);
    else if (statementFlow?.viewOrDownloadAccountStatement) setTab(StatementType.Account);
    else if (statementFlow?.viewOrDownloadCardStatement) setTab(StatementType.Card);

    return () => $statementsStore.events.clear();
  }, []);

  useEffect(() => {
    if (
      !statementFlow?.viewOrDownloadConsolidatedStatement &&
      !statementFlow?.viewOrDownloadAccountStatement &&
      !statementFlow?.viewOrDownloadCardStatement
    ) {
      navigate(RoutePaths.dashboard.onNavigate());
      return;
    }

    $statementsStore.events.getStatements({ StatementType: tab, Currency: currency, FromIndex: stateMap[tab].fromIndex, Count: stateMap[tab].count });
  }, [currency, tab, statementsConsolidatedState, statementsAccountState, statementsCardState]);

  const downloadHandler = (statement: StatementsResponse) => {
    const dateParts = statement.date.split('-');
    $downloadStatementStore.events.downloadStatement({
      id: statement.statementId,
      statementType: statement.statementType,
      year: dateParts[0],
      monthNumber: dateParts[1],
      relationshipNumber: statement.relationshipNumber,
    });
  };

  const tabs = useMemo(
    () => [
      { label: t('card.tabs.consolidated'), value: StatementType.Consolidated },
      { label: t('card.tabs.account'), value: StatementType.Account },
      { label: t('card.tabs.card'), value: StatementType.Card },
    ],
    [t],
  );

  if (
    !statementFlow?.viewOrDownloadConsolidatedStatement &&
    !statementFlow?.viewOrDownloadAccountStatement &&
    !statementFlow?.viewOrDownloadCardStatement
  )
    return null;
  return (
    <div
      className={cn(PaletteUtils.isVcfcPalette && 'rounded-xl bg-white p-6 dark:rounded-xl dark:border dark:border-gray-black dark:bg-soft-black')}
    >
      <div className={cn('flex flex-nowrap items-center justify-between', PaletteUtils.isVcfcPalette && 'max-md:flex-col-reverse')}>
        <Tabs
          className='w-full'
          listClassName={cn(PaletteUtils.isInsigniaPalette && 'gap-0', PaletteUtils.isVcfcPalette && 'gap-12')}
          labelClassName={cn(PaletteUtils.isInsigniaPalette && 'pt-4 px-6 pb-7 bg-white px-6 dark:bg-soft-black')}
          options={tabs}
          value={tab}
          onChange={setTab}
        />
        <div className={cn('flex items-center justify-end pb-6', PaletteUtils.isVcfcPalette && 'max-md:ml-auto max-md:w-full')}>
          <Select
            className={cn('min-w-[160px]', PaletteUtils.isVcfcPalette && 'max-md:min-w-full')}
            items={currenciesAvailableStore.data}
            nullValue={{ isEnable: true, text: t('placeholders.allCurrencies', { ns: 'common' }) }}
            value={currency}
            onChange={setCurrency}
          />
        </div>
      </div>
      {tab === StatementType.Consolidated && statementFlow?.viewOrDownloadConsolidatedStatement && (
        <ConsolidateTable
          isLoading={statementsStore.loading}
          error={statementsStore.error}
          onRefresh={() =>
            $statementsStore.events.getStatements({
              StatementType: tab,
              Currency: currency,
              FromIndex: stateMap[tab].fromIndex,
              Count: stateMap[tab].count,
            })
          }
          statements={statementsStore.data.data}
          onDownloadStatement={downloadHandler}
          downloadingIds={downloadStatementStore.loadingIds}
          totalItemCount={statementsStore.data.total}
          currentPage={statementsConsolidatedState.page}
          countItemPage={statementsConsolidatedState.count}
          onChangePage={statementsConsolidatedState.onChangePage}
          onChangeCountItemPage={statementsConsolidatedState.onChangeCount}
        />
      )}
      {tab === StatementType.Account && statementFlow?.viewOrDownloadAccountStatement && (
        <AccountTable
          isLoading={statementsStore.loading}
          error={statementsStore.error}
          onRefresh={() =>
            $statementsStore.events.getStatements({
              StatementType: tab,
              Currency: currency,
              FromIndex: stateMap[tab].fromIndex,
              Count: stateMap[tab].count,
            })
          }
          statements={statementsStore.data.data}
          onDownloadStatement={downloadHandler}
          downloadingIds={downloadStatementStore.loadingIds}
          totalItemCount={statementsStore.data.total}
          currentPage={statementsAccountState.page}
          countItemPage={statementsAccountState.count}
          onChangePage={statementsAccountState.onChangePage}
          onChangeCountItemPage={statementsAccountState.onChangeCount}
        />
      )}
      {tab === StatementType.Card && statementFlow?.viewOrDownloadCardStatement && (
        <CardTable
          isLoading={statementsStore.loading}
          error={statementsStore.error}
          onRefresh={() =>
            $statementsStore.events.getStatements({
              StatementType: tab,
              Currency: currency,
              FromIndex: stateMap[tab].fromIndex,
              Count: stateMap[tab].count,
            })
          }
          statements={statementsStore.data.data}
          onDownloadStatement={downloadHandler}
          downloadingIds={downloadStatementStore.loadingIds}
          totalItemCount={statementsStore.data.total}
          currentPage={statementsCardState.page}
          countItemPage={statementsCardState.count}
          onChangePage={statementsCardState.onChangePage}
          onChangeCountItemPage={statementsCardState.onChangeCount}
        />
      )}
    </div>
  );
};
